import React, { forwardRef } from "react";
import type { SnackbarKey } from "notistack";
import { SnackbarContent, useSnackbar } from "notistack";
import _ from "underscore";

import type { BoxProps } from "@braintrust/braintrust-ui-components";
import { Box, Button, Typography } from "@braintrust/braintrust-ui-components";

export type SnackbarToastProps = Partial<
  Omit<BoxProps, "ref" | "id" | "content">
> & {
  header: React.ReactNode;
  content?:
    React.ReactNode | ((closeSnackbar: VoidFunction) => React.ReactNode);
  buttonText?: string;
};

// Credits: https://notistack.com/v2.x/examples/advanced/custom-component
export const ToastSnackbarContent = forwardRef<
  HTMLDivElement,
  SnackbarToastProps & { id: SnackbarKey }
>(({ header, content, buttonText = "Okay", ...props }, ref) => {
  const { id, sx, ...rest } = props;
  const { closeSnackbar } = useSnackbar();

  return (
    <SnackbarContent ref={ref}>
      <Box
        sx={{
          bgcolor: "var(--medium-yellow)",
          p: 3,
          maxWidth: "410px",
          boxShadow: "var(--elevation-4)",
          borderRadius: "10px",
          ...sx,
        }}
        {...rest}
      >
        <Typography
          component="p"
          variant="label"
          sx={{ overflowWrap: "anywhere", mb: 1 }}
          size="large"
        >
          {header}
        </Typography>
        <Typography component="p" sx={{ overflowWrap: "anywhere" }}>
          {_.isFunction(content) ? content(() => closeSnackbar(id)) : content}
        </Typography>
        {!!buttonText && (
          <Box sx={{ textAlign: "right", mt: 1.5 }}>
            <Button variant="primary" onClick={() => closeSnackbar(id)}>
              {buttonText}
            </Button>
          </Box>
        )}
      </Box>
    </SnackbarContent>
  );
});
